import React, { useState, useEffect } from 'react';
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  BookOpen,
  Plus,
  Search,
  ExternalLink,
  Settings,
  Shield,
  RefreshCw,
  Trash2,
  Calendar,
  Sparkles,
  ChevronRight,
  TrendingUp,
  Award,
  AlertCircle,
  CheckCircle2,
  X,
  Loader2,
} from 'lucide-react';
import { MentorAreaConfig, MentorStudent } from '../../types/mentorship';
import { mentorshipService } from '../../services/mentorshipService';
import { useAuth } from '../../contexts/AuthContext';

interface MentorshipDashboardPageProps {
  config: MentorAreaConfig;
  onOpenAdminPanel: (tab?: 'geral' | 'hero' | 'modulos' | 'eventos' | 'suporte' | 'alunos' | 'bio') => void;
  onUpdateConfig?: (newConfig: MentorAreaConfig) => void;
}

export const MentorshipDashboardPage: React.FC<MentorshipDashboardPageProps> = ({
  config,
  onOpenAdminPanel,
  onUpdateConfig,
}) => {
  const { user, isAdmin } = useAuth();
  const accentColor = config.branding?.accentColor || '#0FFF55';

  const [students, setStudents] = useState<MentorStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'expired'>('all');

  // Modal de Liberar Acesso
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newStudentName, setNewStudentName] = useState('');
  const [newStudentEmail, setNewStudentEmail] = useState('');
  const [newStudentDays, setNewStudentDays] = useState<number>(config.accessDurationDays || 365);
  const [isSubmittingStudent, setIsSubmittingStudent] = useState(false);
  const [actionFeedback, setActionFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Carregar lista consolidada de alunos
  const loadStudents = async () => {
    setLoading(true);
    try {
      const list = await mentorshipService.getMentorStudents(config.id, config.students);
      setStudents(list);
    } catch (err) {
      console.error('Erro ao carregar alunos:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStudents();
  }, [config.id, config.slug]);

  // Cálculos de métricas
  const totalStudents = students.length;
  const activeStudents = students.filter((s) => s.status !== 'expired').length;
  const expiredStudents = students.filter((s) => s.status === 'expired').length;
  
  const totalModules = config.modules?.length || 0;
  const totalLessons = config.modules?.reduce((acc, m) => acc + (m.lessons?.length || 0), 0) || 0;

  const averageProgress = totalStudents > 0
    ? Math.round(students.reduce((acc, s) => acc + (s.progressPercentage || 0), 0) / totalStudents)
    : 0;

  // Filtragem
  const filteredStudents = students.filter((s) => {
    const matchesSearch =
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (!matchesSearch) return false;

    if (statusFilter === 'active') return s.status !== 'expired';
    if (statusFilter === 'expired') return s.status === 'expired';
    return true;
  });

  // Liberar acesso manual
  const handleGrantAccess = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStudentName.trim() || !newStudentEmail.trim()) {
      setActionFeedback({ type: 'error', message: 'Preencha nome e email do aluno.' });
      return;
    }

    setIsSubmittingStudent(true);
    setActionFeedback(null);

    try {
      const res = await mentorshipService.grantStudentAccess(
        {
          id: config.id,
          name: config.mentorName,
          slug: config.slug,
          access_duration_days: config.accessDurationDays,
        },
        {
          name: newStudentName,
          email: newStudentEmail,
          durationDays: Number(newStudentDays),
        }
      );

      if (res.success && res.student) {
        setActionFeedback({ type: 'success', message: res.message });
        setStudents((prev) => [res.student!, ...prev.filter((s) => s.email.toLowerCase() !== res.student!.email.toLowerCase())]);
        setNewStudentName('');
        setNewStudentEmail('');
        setTimeout(() => {
          setIsAddModalOpen(false);
          setActionFeedback(null);
        }, 1500);
      } else {
        setActionFeedback({ type: 'error', message: res.message || 'Erro ao conceder acesso.' });
      }
    } catch (err: any) {
      setActionFeedback({ type: 'error', message: err?.message || 'Erro ao liberar acesso.' });
    } finally {
      setIsSubmittingStudent(false);
    }
  };

  // Revogar acesso
  const handleRevoke = async (student: MentorStudent) => {
    if (!confirm(`Deseja realmente revogar o acesso de ${student.name} (${student.email})?`)) return;

    try {
      const res = await mentorshipService.revokeStudentAccess(config.id, config.slug, student.email);
      if (res.success) {
        setStudents((prev) =>
          prev.map((s) => (s.email.toLowerCase() === student.email.toLowerCase() ? { ...s, status: 'expired' } : s))
        );
      }
    } catch (err) {
      console.error('Erro ao revogar acesso:', err);
    }
  };

  // Estender acesso
  const handleExtend = async (student: MentorStudent, days: number) => {
    try {
      const res = await mentorshipService.extendStudentAccess(config.id, config.slug, student.email, days);
      if (res.success) {
        await loadStudents();
      }
    } catch (err) {
      console.error('Erro ao estender acesso:', err);
    }
  };

  // Calcular dias restantes
  const getRemainingDaysText = (student: MentorStudent) => {
    if (student.status === 'expired') return { text: 'Acesso Expirado', isWarning: true };
    if (!student.expiresAt) return { text: 'Acesso Vitalício', isWarning: false };

    const now = Date.now();
    const expiry = new Date(student.expiresAt).getTime();
    const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return { text: 'Expirou hoje', isWarning: true };
    if (diffDays === 1) return { text: '1 dia restante', isWarning: true };
    return { text: `${diffDays} dias restantes`, isWarning: diffDays < 15 };
  };

  const bioUrl = `/bio-${config.slug}`;

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-16">
      {/* 1. Header com boas-vindas e ações rápidas */}
      <div className="relative overflow-hidden bg-gradient-to-r from-[#17181c] via-[#15161a] to-[#0d0e11] border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#0FFF55]/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
          <div className="flex items-start sm:items-center gap-5">
            <div className="relative">
              {config.mentorAvatarUrl ? (
                <img
                  src={config.mentorAvatarUrl}
                  alt={config.mentorName}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover border-2 border-white/10 shadow-lg"
                />
              ) : (
                <div
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center font-black text-2xl text-black shadow-lg"
                  style={{ backgroundColor: accentColor }}
                >
                  {config.mentorName?.substring(0, 2).toUpperCase() || 'MC'}
                </div>
              )}
              <div
                className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-[#121316] flex items-center justify-center"
                style={{ backgroundColor: accentColor }}
                title="Mentor Verificado"
              >
                <CheckCircle2 className="w-3.5 h-3.5 text-black" />
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-black text-white tracking-tight">
                  Painel da Mentoria
                </h1>
                <span
                  className="px-2.5 py-0.5 rounded-full text-[11px] font-bold tracking-wider uppercase border border-white/10 bg-white/5"
                  style={{ color: accentColor }}
                >
                  {config.slug}
                </span>
              </div>
              <p className="text-sm text-gray-400 mt-1">
                Gerencie seus mentorados, valide acessos e edite o conteúdo da sua mentoria livremente.
              </p>
            </div>
          </div>

          {/* Quick Action Buttons */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <button
              onClick={() => onOpenAdminPanel('modulos')}
              className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-white rounded-xl text-xs font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              <Settings className="w-4 h-4 text-gray-400" />
              <span>Editar Conteúdo</span>
            </button>

            <a
              href={bioUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-white rounded-xl text-xs font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              <ExternalLink className="w-4 h-4 text-gray-400" />
              <span>Ver Bio Link</span>
            </a>

            <button
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 text-black font-bold rounded-xl text-xs shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{ backgroundColor: accentColor, boxShadow: `0 0 20px ${accentColor}33` }}
            >
              <Plus className="w-4 h-4" />
              <span>Liberar Novo Aluno</span>
            </button>
          </div>
        </div>
      </div>

      {/* 2. KPIs e Métricas Principais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
        {/* Total Alunos */}
        <div className="bg-[#17181c] border border-white/5 hover:border-white/15 rounded-2xl p-5 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Total de Alunos</span>
            <div className="w-8 h-8 rounded-xl bg-blue-500/10 text-blue-400 flex items-center justify-center">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-white mt-3">{totalStudents}</div>
          <p className="text-[11px] text-gray-500 mt-1">Inscritos ou cadastrados</p>
        </div>

        {/* Acessos Ativos */}
        <div className="bg-[#17181c] border border-white/5 hover:border-white/15 rounded-2xl p-5 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Acessos Ativos</span>
            <div className="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
              <UserCheck className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-white mt-3" style={{ color: accentColor }}>
            {activeStudents}
          </div>
          <p className="text-[11px] text-gray-500 mt-1">
            {totalStudents > 0 ? `${Math.round((activeStudents / totalStudents) * 100)}% da base total` : 'Nenhum aluno ativo'}
          </p>
        </div>

        {/* Expirados */}
        <div className="bg-[#17181c] border border-white/5 hover:border-white/15 rounded-2xl p-5 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Acessos Expirados</span>
            <div className="w-8 h-8 rounded-xl bg-rose-500/10 text-rose-400 flex items-center justify-center">
              <UserX className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-white mt-3">{expiredStudents}</div>
          <p className="text-[11px] text-gray-500 mt-1">Podem ser renovados</p>
        </div>

        {/* Módulos & Aulas */}
        <div className="bg-[#17181c] border border-white/5 hover:border-white/15 rounded-2xl p-5 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Conteúdo Publicado</span>
            <div className="w-8 h-8 rounded-xl bg-purple-500/10 text-purple-400 flex items-center justify-center">
              <BookOpen className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-white mt-3">
            {totalModules} <span className="text-sm font-normal text-gray-400">módulos</span>
          </div>
          <p className="text-[11px] text-gray-500 mt-1">{totalLessons} aulas cadastradas</p>
        </div>
      </div>

      {/* 3. Barra de Engajamento & Progresso */}
      {totalStudents > 0 && (
        <div className="bg-[#17181c] border border-white/5 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0FFF55]/10 text-[#0FFF55] flex items-center justify-center shrink-0">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Progresso Médio dos Alunos</h3>
              <p className="text-xs text-gray-400">Percentual de aulas concluídas pela turma</p>
            </div>
          </div>

          <div className="flex items-center gap-4 w-full sm:w-72">
            <div className="flex-1 bg-white/10 h-2.5 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${averageProgress}%`, backgroundColor: accentColor }}
              />
            </div>
            <span className="text-xs font-black text-white w-10 text-right">{averageProgress}%</span>
          </div>
        </div>
      )}

      {/* 4. Tabela / Lista de Gestão de Alunos */}
      <div className="bg-[#17181c] border border-white/10 rounded-3xl p-6 sm:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-white">Mentorados & Acessos</h2>
            <p className="text-xs text-gray-400">Controle individual de acessos e prazos de validade</p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Search Input */}
            <div className="relative min-w-[220px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3.5 py-2 bg-[#121316] border border-white/10 rounded-xl text-xs text-white placeholder:text-gray-500 focus:outline-none focus:border-white/30"
              />
            </div>

            {/* Filter Tabs */}
            <div className="flex items-center bg-[#121316] p-1 rounded-xl border border-white/10 text-xs">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-3 py-1.5 rounded-lg font-medium transition-all ${
                  statusFilter === 'all' ? 'bg-white/10 text-white font-bold' : 'text-gray-400 hover:text-white'
                }`}
              >
                Todos ({students.length})
              </button>
              <button
                onClick={() => setStatusFilter('active')}
                className={`px-3 py-1.5 rounded-lg font-medium transition-all ${
                  statusFilter === 'active' ? 'bg-white/10 text-white font-bold' : 'text-gray-400 hover:text-white'
                }`}
              >
                Ativos ({activeStudents})
              </button>
              <button
                onClick={() => setStatusFilter('expired')}
                className={`px-3 py-1.5 rounded-lg font-medium transition-all ${
                  statusFilter === 'expired' ? 'bg-white/10 text-white font-bold' : 'text-gray-400 hover:text-white'
                }`}
              >
                Expirados ({expiredStudents})
              </button>
            </div>

            <button
              onClick={loadStudents}
              disabled={loading}
              title="Recarregar alunos"
              className="p-2 bg-[#121316] hover:bg-white/5 border border-white/10 text-gray-400 hover:text-white rounded-xl transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-[#0FFF55]' : ''}`} />
            </button>
          </div>
        </div>

        {/* Student Cards / Table */}
        {loading ? (
          <div className="py-16 flex flex-col items-center justify-center text-gray-400 gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-[#0FFF55]" />
            <span className="text-xs">Carregando lista de mentorados...</span>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="py-16 text-center bg-[#121316]/50 border border-dashed border-white/10 rounded-2xl space-y-3">
            <Users className="w-10 h-10 text-gray-600 mx-auto" />
            <div className="text-sm font-bold text-gray-300">Nenhum mentorado encontrado</div>
            <p className="text-xs text-gray-500 max-w-sm mx-auto">
              {searchQuery
                ? 'Nenhum resultado corresponde aos termos da busca digitada.'
                : 'Você ainda não possui mentorados cadastrados nesta mentoria. Clique em "Liberar Novo Aluno" para conceder o primeiro acesso!'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="mt-2 px-4 py-2 bg-[#0FFF55] hover:bg-[#0dd149] text-black font-bold text-xs rounded-xl"
              >
                Liberar Primeiro Aluno
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredStudents.map((student) => {
              const remaining = getRemainingDaysText(student);
              const isExpired = student.status === 'expired';

              return (
                <div
                  key={student.id || student.email}
                  className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-[#121316] hover:bg-[#141519] border border-white/5 hover:border-white/15 rounded-2xl gap-4 transition-all"
                >
                  {/* Aluno Info */}
                  <div className="flex items-center gap-3.5 min-w-[260px]">
                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-white font-black flex items-center justify-center shrink-0">
                      {student.name.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white flex items-center gap-2">
                        <span>{student.name}</span>
                        {isExpired ? (
                          <span className="px-2 py-0.5 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded-md text-[10px] font-bold">
                            Expirado
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-md text-[10px] font-bold">
                            Ativo
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400">{student.email}</div>
                    </div>
                  </div>

                  {/* Detalhes de Acesso */}
                  <div className="flex items-center gap-6 text-xs text-gray-400 flex-wrap">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-gray-500" />
                      <span>Início: <strong className="text-gray-300">{student.accessDateText || 'Hoje'}</strong></span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-gray-500" />
                      <span className={remaining.isWarning ? 'text-amber-400 font-semibold' : 'text-gray-300'}>
                        {remaining.text}
                      </span>
                    </div>

                    {/* Progresso */}
                    <div className="flex items-center gap-2 w-28">
                      <div className="flex-1 bg-white/10 h-1.5 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${student.progressPercentage || 0}%`,
                            backgroundColor: accentColor,
                          }}
                        />
                      </div>
                      <span className="text-[11px] font-bold text-gray-300">
                        {student.progressPercentage || 0}%
                      </span>
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="flex items-center gap-2 self-end md:self-center">
                    <button
                      onClick={() => handleExtend(student, 30)}
                      title="Estender acesso por +30 dias"
                      className="px-2.5 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-semibold text-white rounded-lg transition-colors"
                    >
                      +30 Dias
                    </button>
                    <button
                      onClick={() => handleExtend(student, 365)}
                      title="Estender acesso por +1 ano"
                      className="px-2.5 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-semibold text-white rounded-lg transition-colors"
                    >
                      +1 Ano
                    </button>
                    <button
                      onClick={() => handleRevoke(student)}
                      title="Revogar acesso deste aluno"
                      className="p-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 5. Modal de Concessão de Acesso Manual */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-[#17181c] border border-white/10 w-full max-w-md rounded-3xl p-6 sm:p-7 space-y-5 shadow-2xl relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div
                  className="w-8 h-8 rounded-xl flex items-center justify-center text-black font-bold"
                  style={{ backgroundColor: accentColor }}
                >
                  <Plus className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">Liberar Acesso de Aluno</h3>
                  <p className="text-xs text-gray-400">Conceder acesso manual à mentoria</p>
                </div>
              </div>

              <button
                onClick={() => setIsAddModalOpen(false)}
                className="w-8 h-8 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {actionFeedback && (
              <div
                className={`p-3 rounded-xl text-xs flex items-center gap-2 ${
                  actionFeedback.type === 'success'
                    ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                    : 'bg-rose-500/10 border border-rose-500/20 text-rose-400'
                }`}
              >
                {actionFeedback.type === 'success' ? (
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 shrink-0" />
                )}
                <span>{actionFeedback.message}</span>
              </div>
            )}

            <form onSubmit={handleGrantAccess} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-300">Nome do Aluno</label>
                <input
                  type="text"
                  placeholder="Ex: Carlos Oliveira"
                  value={newStudentName}
                  onChange={(e) => setNewStudentName(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-[#121316] border border-white/10 rounded-xl text-xs text-white placeholder:text-gray-500 focus:outline-none focus:border-white/30"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-300">E-mail do Aluno</label>
                <input
                  type="email"
                  placeholder="Ex: carlos@email.com"
                  value={newStudentEmail}
                  onChange={(e) => setNewStudentEmail(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-[#121316] border border-white/10 rounded-xl text-xs text-white placeholder:text-gray-500 focus:outline-none focus:border-white/30"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-300">Duração do Acesso (dias)</label>
                <div className="grid grid-cols-4 gap-2">
                  {[30, 90, 180, 365].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setNewStudentDays(d)}
                      className={`py-2 rounded-xl text-xs font-bold border transition-all ${
                        newStudentDays === d
                          ? 'border-[#0FFF55] bg-[#0FFF55]/10 text-[#0FFF55]'
                          : 'border-white/10 bg-[#121316] text-gray-400 hover:text-white'
                      }`}
                    >
                      {d} dias
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-2 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white text-xs font-medium rounded-xl transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingStudent}
                  className="flex items-center gap-2 px-5 py-2 text-black font-bold text-xs rounded-xl shadow-lg transition-all"
                  style={{ backgroundColor: accentColor }}
                >
                  {isSubmittingStudent ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Liberando...</span>
                    </>
                  ) : (
                    <span>Confirmar e Liberar Acesso</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
